import { entityTypes } from "@/src/constants/entityTypeConstants.js";
import {
  CRM_SERVICES,
  INVOICES_SLUG,
  LINE_ITEMS_SLUG,
  PRODUCTS_SLUG,
  EMPLOYEES_SLUG,
  DEPARTMENTS_SLUG,
  TEAMS_SLUG,
  ROLES_SLUG,
  ONBOARDING_SLUG,
  OFFBOARDING_SLUG,
} from "@/src/constants/crmConstants.js";
import { withServiceQuery } from "@/src/utils/serviceUrlUtils.js";

export const QA_BASE_PATH = "/developer_tools/ai-qa";

// Lazy-initialized to avoid a circular-import TDZ with crmConstants.js.
let _serviceScopedPaths = null;
const getServiceScopedPaths = () => {
  if (_serviceScopedPaths) return _serviceScopedPaths;
  _serviceScopedPaths = {
    [PRODUCTS_SLUG]: {
      service: CRM_SERVICES.PRODUCTS,
      path: (id) => `/${PRODUCTS_SLUG}/${id}`,
    },
    [INVOICES_SLUG]: {
      service: CRM_SERVICES.FINANCE,
      path: (id) => `/${INVOICES_SLUG}/${id}`,
    },
    [LINE_ITEMS_SLUG]: {
      service: CRM_SERVICES.INVOICES,
      path: (id) => `/${INVOICES_SLUG}/line-items/${id}`,
    },
    [EMPLOYEES_SLUG]: {
      service: CRM_SERVICES.HR,
      path: (id) => `/people/${EMPLOYEES_SLUG}/${id}`,
    },
    [DEPARTMENTS_SLUG]: {
      service: CRM_SERVICES.HR,
      path: (id) => `/people/${DEPARTMENTS_SLUG}/${id}`,
    },
    [TEAMS_SLUG]: {
      service: CRM_SERVICES.HR,
      path: (id) => `/people/${TEAMS_SLUG}/${id}`,
    },
    [ROLES_SLUG]: {
      service: CRM_SERVICES.HR,
      path: (id) => `/people/${ROLES_SLUG}/${id}`,
    },
    [ONBOARDING_SLUG]: {
      service: CRM_SERVICES.HR,
      path: (id) => `/people/${ONBOARDING_SLUG}/${id}`,
    },
    [OFFBOARDING_SLUG]: {
      service: CRM_SERVICES.HR,
      path: (id) => `/people/${OFFBOARDING_SLUG}/${id}`,
    },
  };
  return _serviceScopedPaths;
};

const appendServiceQuery = (path, service) => withServiceQuery(path, service);

export const NO_DETAIL_REDIRECT_SLUGS = new Set([
  "goals",
  "okrs",
  "shifts",
  "shift_mappings",
  "holidays",
]);

export const shouldSkipDetailRedirect = (slug) =>
  NO_DETAIL_REDIRECT_SLUGS.has(String(slug || "").toLowerCase());

export const getEntityDetailRoute = ({
  entityType,
  recordId,
  projectId = null,
  spaceId = null,
  service = null,
}) => {
  if (!recordId) return "#";

  const lowerEntityType = String(entityType).toLowerCase();

  const scoped = getServiceScopedPaths()[lowerEntityType];
  if (scoped) {
    if (!service || service === scoped.service) {
      return appendServiceQuery(scoped.path(recordId), scoped.service);
    }
    return appendServiceQuery(`/crm/${lowerEntityType}/${recordId}`, service);
  }

  const routeMap = {
    contacts: `/contacts/${recordId}`,
    companies: `/companies/${recordId}`,
    deals: `/deals/${recordId}`,
    leads: `/leads/${recordId}`,
    [entityTypes.TICKETS]: `/tickets/${recordId}`,
    qa_tests: `${QA_BASE_PATH}/tests/${recordId}`,
    qa_test_runs: `${QA_BASE_PATH}/test-runs/${recordId}`,
    qa_test_steps: `${QA_BASE_PATH}/test-steps/${recordId}`,
    qa_issues: `${QA_BASE_PATH}/issues/${recordId}`,
  };

  if (routeMap[lowerEntityType]) {
    return routeMap[lowerEntityType];
  }

  if (lowerEntityType === entityTypes.TASKS && projectId) {
    return `/spaces/projects/${projectId}/tasks/${recordId}`;
  }

  if (lowerEntityType === entityTypes.CRM_TASKS || lowerEntityType === "crm_tasks") {
    return `/crm/tasks/${recordId}`;
  }

  return appendServiceQuery(`/crm/${lowerEntityType}/${recordId}`, service);
};

export const getQAParentRoute = (slug, entity) => {
  if (slug === "qa_tests" || slug === "qa_test_runs") {
    const suiteId = entity?.suite_id;
    return suiteId ? `${QA_BASE_PATH}/suites/${suiteId}` : QA_BASE_PATH;
  }
  if (slug === "qa_test_steps" || slug === "qa_issues") {
    const testId = entity?.test_id;
    return testId ? `${QA_BASE_PATH}/tests/${testId}` : QA_BASE_PATH;
  }
  if (slug === "qa_test_run_results") {
    const testRunId = entity?.test_run_id;
    return testRunId ? `${QA_BASE_PATH}/test-runs/${testRunId}` : QA_BASE_PATH;
  }
  return null;
};

export const navigateToEntityDetail = ({
  router,
  entityType,
  recordId,
  projectId = null,
  spaceId = null,
  service = null,
}) => {
  const route = getEntityDetailRoute({
    entityType,
    recordId,
    projectId,
    spaceId,
    service,
  });

  if (route !== "#") {
    router.push(route);
  }
};
